import { z } from 'zod'
import { TRAINER_CONTRACT_VERSION } from './trainer-event-read'

export const TRAINER_CERTIFICATE_READ_CONTRACT_VERSION = TRAINER_CONTRACT_VERSION

const optionalQueryString = z.preprocess(
  (value) => (typeof value === 'string' ? value : undefined),
  z.string().optional(),
)

/** Query keys accepted by the authenticated Trainer certificate read routes. */
export const trainerCertificateReadQuerySchema = z.object({
  orgId: optionalQueryString,
  provider: optionalQueryString,
  status: optionalQueryString,
  providerMode: optionalQueryString,
  limit: optionalQueryString,
}).passthrough()

export const trainerCertificateReadSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  rawEmailReturned: z.literal(false),
  rawPayloadReturned: z.literal(false),
  providerTouched: z.literal(false),
}).passthrough()

export const trainerCertificateWorkshopConfigSchema = z.object({
  key: z.string().min(1),
  workshopName: z.string().min(1),
  courseCanonicalKey: z.string().min(1),
  certificateTemplateId: z.string().min(1),
  groupWorkTemplateId: z.string().min(1),
  memberpressMembershipId: z.number().int().positive(),
  thinkificMembershipId: z.number().int().positive(),
}).passthrough()

export const trainerCertificateWorkshopConfigListResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CERTIFICATE_READ_CONTRACT_VERSION),
  workshopConfigs: z.array(trainerCertificateWorkshopConfigSchema),
  sideEffects: z.object({
    db1Read: z.literal(false),
    db1Written: z.literal(false),
    providerTouched: z.literal(false),
  }).passthrough(),
}).passthrough()

export const trainerCertificateWebhookReadinessResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CERTIFICATE_READ_CONTRACT_VERSION),
  certificateWebhookReadiness: z.object({
    owner: z.string().min(1),
    callbackUrl: z.string().min(1),
    route: z.string().min(1),
    acceptedContentTypes: z.array(z.string().min(1)),
    secretHeaderNames: z.array(z.string().min(1)),
    webhookSecretConfigured: z.boolean(),
    dashboardCallbackOwnership: z.object({
      status: z.string().min(1),
      legacyXanoCallbacksRetireReady: z.boolean(),
      requiredProof: z.string().min(1),
      rollbackPlan: z.string().min(1),
    }).passthrough(),
    providerMode: z.string().min(1),
    webhookProviderEnqueue: z.object({}).passthrough(),
    providerReadiness: z.object({}).passthrough(),
    sideEffects: z.object({
      db1Read: z.literal(false),
      db1Written: z.literal(false),
      providerTouched: z.literal(false),
      secretReturned: z.literal(false),
      rawPayloadReturned: z.literal(false),
    }).passthrough(),
  }).passthrough(),
}).passthrough()

export const trainerCertificateParticipantSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  emailReturned: z.literal(false),
}).passthrough()

export const trainerCertificateLookupSummarySchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  eventId: z.string().min(1).nullable(),
  uid: z.string().min(1),
  formId: z.string().min(1),
  sourceTable: z.string().min(1),
  workshopKey: z.string().min(1),
  workshopName: z.string().min(1),
  courseTypeId: z.string().nullable(),
  certificateDate: z.string().nullable(),
  participant: trainerCertificateParticipantSchema,
  trainerId: z.string().nullable(),
  location: z.string().nullable(),
  organization: z.string().nullable(),
  designation: z.string().nullable(),
  homeCity: z.string().nullable(),
  contentRating: z.string().nullable(),
  trainerRating: z.string().nullable(),
  heading: z.string().nullable(),
  text: z.string(),
  status: z.string().min(1),
  providerMode: z.string().min(1),
  emailStatus: z.string().min(1),
  memberpressStatus: z.string().min(1),
  thinkificStatus: z.string().min(1),
  kindeStatus: z.string().min(1),
  providerStatus: z.record(z.unknown()),
  idempotencyKey: z.string().optional(),
  createdAt: z.string().min(1),
  updatedAt: z.string().min(1),
}).passthrough()

export const trainerCertificateLookupResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CERTIFICATE_READ_CONTRACT_VERSION),
  certificate: trainerCertificateLookupSummarySchema,
  sideEffects: trainerCertificateReadSideEffectsSchema,
}).passthrough()

export const trainerCertificateProviderOutboxRowSchema = z.object({
  id: z.string().min(1),
  orgId: z.string().min(1),
  sourceEntityType: z.string().min(1),
  sourceEntityId: z.string().min(1),
  provider: z.string().min(1),
  actionType: z.string().min(1),
  status: z.string().min(1),
  providerMode: z.string().min(1),
  idempotencyKey: z.string().min(1),
  payloadHash: z.string().min(1),
  attemptCount: z.number().int().nonnegative(),
  maxAttempts: z.number().int().positive(),
  nextAttemptAt: z.string().nullable(),
  providerAuditId: z.string().nullable(),
  lastErrorCode: z.string().nullable(),
  lastErrorSummary: z.string().nullable(),
  createdAt: z.string().nullable(),
  updatedAt: z.string().nullable(),
}).passthrough()

export const trainerCertificateProviderOutboxResponseSchema = z.object({
  contract_version: z.literal(TRAINER_CERTIFICATE_READ_CONTRACT_VERSION),
  export: z.object({
    surface: z.literal('trainer_certificate_provider_outbox'),
    generatedAt: z.string().min(1),
    format: z.literal('json_redacted'),
    rowCount: z.number().int().nonnegative(),
    filters: z.object({
      orgId: z.union([z.string(), z.number().int()]).nullable(),
      provider: z.string().nullable(),
      status: z.string().nullable(),
      providerMode: z.string().nullable(),
      limit: z.number().int().positive(),
    }).passthrough(),
    byProvider: z.record(z.number().int().nonnegative()),
    byStatus: z.record(z.number().int().nonnegative()),
    providerTouched: z.literal(false),
    rawPayloadReturned: z.literal(false),
    rawEmailReturned: z.literal(false),
    secretReturned: z.literal(false),
    manualExecutionBoundary: z.string().min(1),
  }).passthrough(),
  rows: z.array(trainerCertificateProviderOutboxRowSchema),
}).passthrough()

export type TrainerCertificateReadQuery = z.infer<typeof trainerCertificateReadQuerySchema>
export type TrainerCertificateReadSideEffects = z.infer<typeof trainerCertificateReadSideEffectsSchema>
export type TrainerCertificateWorkshopConfig = z.infer<typeof trainerCertificateWorkshopConfigSchema>
export type TrainerCertificateWorkshopConfigListResponse = z.infer<typeof trainerCertificateWorkshopConfigListResponseSchema>
export type TrainerCertificateWebhookReadinessResponse = z.infer<typeof trainerCertificateWebhookReadinessResponseSchema>
export type TrainerCertificateParticipant = z.infer<typeof trainerCertificateParticipantSchema>
export type TrainerCertificateLookupSummary = z.infer<typeof trainerCertificateLookupSummarySchema>
export type TrainerCertificateLookupResponse = z.infer<typeof trainerCertificateLookupResponseSchema>
export type TrainerCertificateProviderOutboxRow = z.infer<typeof trainerCertificateProviderOutboxRowSchema>
export type TrainerCertificateProviderOutboxResponse = z.infer<typeof trainerCertificateProviderOutboxResponseSchema>
